import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import type { InferType } from 'yup';
import { string, object, number, bool } from 'yup';
import { useDisplayTokens } from '~/hooks';
import { usePostHog } from '~/services/postHog-client';

export const FREE_TOKENS_COUNT = 3;

export const accountSchema = object({
    userId: string().lowercase().trim().required(),
    email: string().lowercase().trim().required(),
    displayName: string().trim().optional(),
    tenantId: string().lowercase().trim().required(),
    tokens: number()
        .required()
        .default(() => FREE_TOKENS_COUNT),
    isUnlimited: bool()
        .required()
        .default(() => false),
    refCode: string().lowercase().trim().required(),
    refBonusBy: string().lowercase().trim().optional(),
});

const applicationContextSchema = object({
    host: string().lowercase().trim().required(),
    platform: string().lowercase().trim().required(),
    account: accountSchema.nullable(),
});

type ApplicationContext = InferType<typeof applicationContextSchema>;
type DecoratedApplicationContext = ApplicationContext & {
    isOffice: boolean;
    isTeams: boolean;
    isPowerPoint: boolean;
    isOffice365: boolean; // office.com
    isWord: boolean;
    isOutlook: boolean;
    isExcel: boolean;
    isIframe: boolean;
    loginUrl: string;
};

const applicationState = atom<ApplicationContext | null>(null);

export function useApplicationContext(): DecoratedApplicationContext | null {
    const [state] = useAtom(applicationState);
    const decoratedApplicationContext = useMemo(() => {
        if (!state) {
            return null;
        }

        const isOffice = state?.platform === 'officeonline' || false;
        const isOffice365 =
            (state?.platform === 'web' && state?.host === 'office') || false;
        const isWord = state?.host === 'word';
        const isPowerPoint = state?.host === 'powerpoint';
        const isExcel = state?.host === 'excel';
        const isTeams = state?.host === 'teams';
        const isOutlook =
            state.platform !== 'teams' &&
            state.platform !== 'web' &&
            state.platform !== 'ios' &&
            state.platform !== 'android' &&
            state?.host === 'outlook';
        const isIframe = state?.host === 'iframe';

        function getTelegramInfo() {
            if (!window?.Telegram?.WebApp) {
                return '';
            }

            const tgApp = window.Telegram.WebApp;
            tgApp.expand();
            tgApp.ready();
            tgApp.disableVerticalSwipes();

            const tgPlatform = tgApp.platform;
            if (tgPlatform === 'unknown') {
                return '';
            }

            return `tg-${tgApp.platform}`;
        }
        const telegramHost = getTelegramInfo();
        const isTelegram = telegramHost || false;

        const sourcePayload = encodeURIComponent(
            [state.platform, state.host, telegramHost]
                .filter((s) => !!s)
                .join('_')
                .toLowerCase()
                .trim(),
        );

        return {
            ...state,
            isOffice,
            isTeams,
            isOffice365,
            isWord,
            isPowerPoint,
            isOutlook,
            isExcel,
            isIframe,
            isTelegram,
            loginUrl: `/${isIframe ? 'iframe' : 'office'}/signin?source=${sourcePayload}`,
        };
    }, [state]);

    return decoratedApplicationContext;
}

export function useSetApplicationContext() {
    const [, setState] = useAtom(applicationState);
    const { setTokens } = useDisplayTokens();
    const postHog = usePostHog();

    const setApplicationContext = useCallback(
        (payload: ApplicationContext) => {
            const state = applicationContextSchema.cast(payload);
            setState(state);
            setTokens(state.account?.tokens || 0);
            postHog.identify(
                (state.account?.email || '').toLowerCase(),
                state.account!,
            );

            function sendConversionToGoogle() {
                window.dataLayer = window.dataLayer || [];
                function gtag() {
                    dataLayer.push(arguments);
                }
                gtag('event', 'conversion', {
                    send_to: 'AW-16850919995/hfCjCLCaqJkaELvEkuM-',
                    value: 1.0,
                    currency: 'USD',
                });
                gtag('event', 'conversion', {
                    send_to: 'AW-16850919995/fiaqCOuKqZkaELvEkuM-',
                    value: 1.0,
                    currency: 'USD',
                });
            }
            sendConversionToGoogle();
        },
        [setState, setTokens, postHog],
    );
    return setApplicationContext;
}
